import {Project, ProjectSexes} from '@shared/api/app_api';

import {createDataStore} from '@shared-web/lib/data_store';

import {auth} from '@src/lib/account';
import {apiCall} from '@src/lib/api';

export type ProjectScores = Record<string, number>;
export type ParsedProject = Omit<Project, 'scores'> & {scores: ProjectScores};

export function parseProject(project: Project): ParsedProject {
  const rawScores = project.scores.split('/');
  const scores: ProjectScores = {};
  for (let i = 0; i < rawScores.length; i += 2) {
    const key = rawScores[i];
    const scoreStr = rawScores[i + 1];
    if (key === undefined || scoreStr === undefined) {
      continue;
    }
    const score = parseFloat(scoreStr);
    if (isNaN(score)) {
      continue;
    }
    scores[key] = score;
  }
  return {...project, scores};
}

function serializeProject(project: ParsedProject): Project {
  return {
    ...project,
    scores: Object.entries(project.scores)
      .map(([key, score]) => `${key}/${score}`)
      .join('/'),
  };
}

const projectsStore = createDataStore<ParsedProject[]>([]);
export const getProjects = projectsStore.getData;
export const setProjects = (projects: ParsedProject[], opts?: SyncOptions): void => {
  projectsStore.setData(projects);
  syncProjects(opts);
};
export const useProjects = projectsStore.useData;

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const uid = (): string => Math.random().toString(36).slice(2);

const chars = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'];
const CODE_LENGTH = 6;
function randomCode(): string {
  let res = '';
  for (let i = 0; i < CODE_LENGTH; i++) {
    res += chars[Math.floor(Math.random() * chars.length)];
  }
  return res;
}

export function createProject(opts: {sexes: ProjectSexes}): void {
  const {sexes} = opts;
  const newProject: Project = {
    id: uid(),
    createdAt: Date.now(),
    updatedAt: Date.now(),
    lastSync: 0,
    scores: '',
    settings: {minRecent: 100, recentYear: 2019, sexes},
    code: randomCode(),
    compare: undefined,
  };
  setProjects([...getProjects(), parseProject(newProject)]);
}

export function setProjectScore(projectId: string, key: string, score: number): void {
  const project = getProjects().find(p => p.id === projectId);
  if (!project) {
    return;
  }
  project.scores[key] = score;
  project.updatedAt = Date.now();
  setProjects([...getProjects()]);
}

export function setProjectCompareCode(projectId: string, code: string | undefined): void {
  const project = getProjects().find(p => p.id === projectId);
  if (!project) {
    return;
  }
  project.compare = code;
  project.updatedAt = Date.now();
  setProjects([...getProjects()]);
}

interface SyncOptions {
  noSyncLocal?: boolean;
  noSyncWeb?: boolean;
}

function syncProjects(options?: SyncOptions): void {
  const {noSyncLocal, noSyncWeb} = options ?? {};
  const projects = getProjects();
  if (!noSyncLocal) {
    localStorage.setItem('projects', JSON.stringify(projects.map(serializeProject)));
  }
  if (!noSyncWeb) {
    for (const project of projects) {
      const {id, lastSync, updatedAt} = project;
      if (lastSync < updatedAt) {
        const syncedProject = {...project, lastSync: Date.now()};
        apiCall('POST /sync-project', serializeProject(syncedProject))
          .then(() => {
            setProjects(
              getProjects().map(p =>
                p.id === id && p.updatedAt === updatedAt ? syncedProject : p
              ),
              {noSyncWeb: true}
            );
          })
          // eslint-disable-next-line @typescript-eslint/no-loop-func
          .catch((err: unknown) => console.error(err));
      }
    }
  }
}

export function loadProjectsLocal(): void {
  const res = localStorage.getItem('projects');
  if (res === null) {
    setProjects([], {noSyncLocal: true, noSyncWeb: true});
    return;
  }
  let noSyncLocal = true;
  const projects = (JSON.parse(res) as Project[]).map(parseProject);
  for (const project of projects) {
    if (project.code === undefined) {
      project.code = randomCode();
      project.updatedAt = Date.now();
      noSyncLocal = false;
    }
  }
  setProjects(projects, {noSyncLocal});
}

export async function loadProjectsWeb(): Promise<Project[]> {
  try {
    const res = await apiCall('GET /list-projects', {});
    return res;
  } catch (err: unknown) {
    if (err instanceof Error && err.message === 'Forbidden') {
      await auth();
      return await loadProjectsWeb();
    }
    throw err;
  }
}

loadProjectsLocal();
loadProjectsWeb()
  .then(projects => {
    const currentProjects = getProjects();
    const currentProjectIds = new Set(currentProjects.map(p => p.id));
    setProjects(
      [...currentProjects, ...projects.filter(p => !currentProjectIds.has(p.id)).map(parseProject)],
      {noSyncWeb: true}
    );
  })
  .catch((err: unknown) => console.error(err));
